import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer, Subscription } from 'rxjs';
import { AuthService } from './auth.service';
import { SessionModalData } from '../components/auth/session-modal/session-modal.component';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SessionManagerService {
  private sessionWarningSubject = new BehaviorSubject<SessionModalData | null>(null);
  private sessionTimerSubscription?: Subscription;
  private warningTimerSubscription?: Subscription;
  private ssoCookieRefreshSubscription?: Subscription;

  public sessionWarning$ = this.sessionWarningSubject.asObservable();

  // Configuration
  private readonly SESSION_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
  private readonly WARNING_TIME = 5 * 60 * 1000; // 5 minutes before expiration
  private readonly CHECK_INTERVAL = 60 * 1000; // Check every minute
  // La cookie SSO (cda_sso_token) se fija con max-age=3600 y nunca se reescribe durante la
  // sesión activa (solo en un login/SSO real) — a diferencia de la sesión de MSAL, que sigue
  // viva mucho más. Sin este refresh, a la hora la cookie desaparece mientras isAuthenticated()
  // sigue en true, y checkSsoCookieStatus() lo interpreta como "cerraste sesión en Invierte"
  // (falso positivo) -> modal cross-app-logout -> logout(true) -> window.location.href='/'.
  // Se renueva bastante antes del vencimiento (45 min de 60) para que nunca llegue a
  // desaparecer mientras la pestaña siga autenticada.
  private readonly SSO_COOKIE_REFRESH_INTERVAL = 45 * 60 * 1000;
  
  private sessionStartTime: number = 0;
  private lastActivityTime: number = 0;

  constructor(private authService: AuthService) {
    this.initializeSessionTracking();
  }

  private initializeSessionTracking(): void {
    // Start polling for SSO cookie changes across both authenticated and unauthenticated states
    this.startGlobalSsoTracking();

    // Listen for authentication status changes
    this.authService.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.startSessionTracking();
      } else {
        this.stopSessionTracking();
      }
    });

    // Track user activity
    this.trackUserActivity();
  }

  private ssoCookieSubscription: any;

  private startGlobalSsoTracking(): void {
    // Wait 5 seconds before the first poll to ensure MSAL has finished processing any redirects
    // Then poll every 3 seconds
    this.ssoCookieSubscription = timer(5000, 3000).subscribe(() => {
      this.checkSsoCookieStatus();
    });
  }

  private startSessionTracking(): void {
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();

    // Start session timer for inactivity checks
    this.sessionTimerSubscription = timer(0, this.CHECK_INTERVAL).subscribe(() => {
      this.checkSessionStatus();
    });

    // Renovar la cookie SSO antes de que expire, para no disparar el falso "cierre cruzado"
    this.ssoCookieRefreshSubscription = timer(this.SSO_COOKIE_REFRESH_INTERVAL, this.SSO_COOKIE_REFRESH_INTERVAL).subscribe(() => {
      this.refreshSsoCookieSilently();
    });
  }

  private refreshSsoCookieSilently(): void {
    const profile = this.authService.getCurrentUser();
    if (!profile) return;

    this.authService.ssoSilent({ loginHint: profile.mail }).subscribe({
      next: () => console.log('Cookie SSO renovada silenciosamente antes de expirar.'),
      error: (error) => console.warn('No se pudo renovar la cookie SSO silenciosamente (se reintentará en el próximo ciclo):', error),
    });
  }

  private checkSsoCookieStatus(): void {
    const isAuth = this.authService.isAuthenticated();
    const cookieName = environment.ssoCookieName;
    const hasCookie = document.cookie.includes(`${cookieName}=`);

    // If we are authenticated but the central cookie was destroyed (e.g., from Invierte logout)
    // We only trigger cross-app-logout if we previously had the cookie, to prevent false positives when running locally without Invierte.
    const hadCookie = sessionStorage.getItem('had_sso_cookie') === 'true';
    
    if (hasCookie && !hadCookie) {
      sessionStorage.setItem('had_sso_cookie', 'true');
    }

    if (isAuth && !hasCookie && hadCookie) {
      console.log(`La cookie SSO (${cookieName}) desapareció (cierre transversal). Mostrando alerta de sesión finalizada...`);
      sessionStorage.removeItem('had_sso_cookie');
      this.stopSessionTracking();
      
      const modalData: SessionModalData = {
        type: 'cross-app-logout',
        message: 'Tu sesión ha sido cerrada desde otra aplicación (Invierte).'
      };
      
      this.sessionWarningSubject.next(modalData);
    } 
    // If we are NOT authenticated, but the central cookie appeared (e.g., from Invierte login)
    else if (!isAuth && hasCookie) {
      console.log(`La cookie SSO (${cookieName}) apareció (inicio transversal). Intentando SSO silencioso...`);
      
      const regex = new RegExp(`(?:^|; )${cookieName}=([^;]+)`);
      const match = document.cookie.match(regex);
      let loginHint = undefined;
      if (match && match[1]) {
        try {
          const payload = JSON.parse(atob(match[1].split('.')[1]));
          loginHint = payload.preferred_username || payload.email || payload.upn;
        } catch (e) {
          console.error('Error extracting loginHint from SSO token', e);
        }
      }

      // Evitar que el timer dispare múltiples intentos de SSO silencioso
      if (this.ssoCookieSubscription) {
        this.ssoCookieSubscription.unsubscribe();
      }

      this.authService.ssoSilent({ loginHint }).subscribe({
        next: (result) => {
          console.log('✅ SSO silencioso exitoso! Sesión sincronizada automáticamente.', result);
          // El estado de autenticación se actualizará automáticamente en AuthService
          // Reiniciar el monitoreo
          this.startGlobalSsoTracking();
        },
        error: (error) => {
          console.error('❌ SSO silencioso falló. Cayendo a modal interactivo:', error);
          
          // Si el silencioso falla (ej. requiere interacción explícita), mostramos el modal original
          const modalData: SessionModalData = {
            type: 'cross-app-login',
            message: 'Se ha detectado un inicio de sesión en otra aplicación. ¿Deseas sincronizar tu sesión aquí también?'
          };
          this.sessionWarningSubject.next(modalData);
          
          // Reiniciar el monitoreo para no quedarnos atascados
          this.startGlobalSsoTracking();
        }
      });
    }
  }

  private stopSessionTracking(): void {
    if (this.sessionTimerSubscription) {
      this.sessionTimerSubscription.unsubscribe();
    }
    if (this.warningTimerSubscription) {
      this.warningTimerSubscription.unsubscribe();
    }
    if (this.ssoCookieRefreshSubscription) {
      this.ssoCookieRefreshSubscription.unsubscribe();
    }
    this.sessionWarningSubject.next(null);
  }

  private checkSessionStatus(): void {
    const now = Date.now();
    // Ventana deslizante: expira por INACTIVIDAD real (sin mousemove/click/scroll/keypress),
    // no por un reloj absoluto desde el login — antes usaba timeSinceStart, que hacía expirar
    // la sesión a la hora exacta del login aunque el usuario hubiera estado activo todo ese
    // tiempo. lastActivityTime ya se actualizaba en cada evento (trackUserActivity), solo no
    // se usaba acá.
    const timeSinceActivity = now - this.lastActivityTime;

    // Check if session has expired
    if (timeSinceActivity >= this.SESSION_DURATION) {
      this.handleSessionExpired();
      return;
    }

    // Check if warning should be shown
    const timeUntilExpiration = this.SESSION_DURATION - timeSinceActivity;
    if (timeUntilExpiration <= this.WARNING_TIME && timeUntilExpiration > 0) {
      this.showSessionWarning(Math.floor(timeUntilExpiration / 1000));
    }
  }

  private handleSessionExpired(customMessage?: string): void {
    this.stopSessionTracking();
    
    const modalData: SessionModalData = {
      type: 'token-expired',
      message: customMessage || 'Tu sesión ha expirado por inactividad'
    };
    
    this.sessionWarningSubject.next(modalData);
  }

  private showSessionWarning(timeRemaining: number): void {
    const modalData: SessionModalData = {
      type: 'session-warning',
      timeRemaining: timeRemaining
    };
    
    this.sessionWarningSubject.next(modalData);
    
    // Start countdown timer
    if (this.warningTimerSubscription) {
      this.warningTimerSubscription.unsubscribe();
    }
    
    this.warningTimerSubscription = timer(0, 1000).subscribe(() => {
      if (timeRemaining > 0) {
        timeRemaining--;
        const updatedModalData: SessionModalData = {
          type: 'session-warning',
          timeRemaining: timeRemaining
        };
        this.sessionWarningSubject.next(updatedModalData);
      } else {
        this.handleSessionExpired();
      }
    });
  }

  private trackUserActivity(): void {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, () => {
        this.updateLastActivity();
      }, { passive: true });
    });
  }

  private updateLastActivity(): void {
    this.lastActivityTime = Date.now();
  }

  // Public methods
  extendSession(): void {
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();
    this.sessionWarningSubject.next(null);
    
    if (this.warningTimerSubscription) {
      this.warningTimerSubscription.unsubscribe();
    }
  }

  showUserProfile(): void {
    const modalData: SessionModalData = {
      type: 'user-profile'
    };
    
    this.sessionWarningSubject.next(modalData);
  }

  dismissModal(): void {
    this.sessionWarningSubject.next(null);
    
    if (this.warningTimerSubscription) {
      this.warningTimerSubscription.unsubscribe();
    }
  }

  // Get session information
  getSessionInfo(): {
    isActive: boolean;
    startTime: Date;
    lastActivity: Date;
    timeRemaining: number;
  } {
    const now = Date.now();
    const timeRemaining = Math.max(0, this.SESSION_DURATION - (now - this.sessionStartTime));
    
    return {
      isActive: this.authService.isAuthenticated(),
      startTime: new Date(this.sessionStartTime),
      lastActivity: new Date(this.lastActivityTime),
      timeRemaining: Math.floor(timeRemaining / 1000)
    };
  }

  // Force session refresh
  refreshSession(): Observable<string> {
    return new Observable(observer => {
      this.authService.refreshToken().subscribe({
        next: (token) => {
          this.extendSession();
          observer.next(token);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }
}